import { ChangeEvent, useState, MouseEvent, useMemo, useRef, useEffect, useCallback } from 'react';

import { useDebounce } from '/hooks';

import { ISearchList, TrieNode } from '../types';

import { SearchInput } from './SearchInput';
import { MAX_ITEMS, createTrie, searchTrie } from './constants';

export const SearchList = <T,>({ placeholder, SearchRender, prefixSearch, itemList, renderList }: ISearchList<T>) => {
  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value, 300);

  const trieRef = useRef<TrieNode | null>(null);
  useEffect(() => {
    if (prefixSearch && itemList.length > MAX_ITEMS) {
      trieRef.current = createTrie(itemList);
    }
  }, [itemList, prefixSearch]);

  const onSearchInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    setValue(searchTerm.trim());
  }, []);

  const onClearClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setValue('');
  }, []);

  const filteredList = useMemo(() => {
    if (!debouncedValue) return itemList;
    return trieRef.current
      ? searchTrie<T>(trieRef.current, debouncedValue)
      : itemList.filter((item) => item.label.toLowerCase().includes(debouncedValue.toLowerCase()));
  }, [debouncedValue, itemList]);

  const renderVirtualizedList = useMemo(() => renderList(filteredList), [renderList, filteredList]);

  return (
    <>
      {SearchRender ? (
        SearchRender({ value, onClearClick, onSearchInput })
      ) : (
        <SearchInput value={value} placeholder={placeholder} onClear={onClearClick} onChange={onSearchInput} />
      )}

      {filteredList.length > 0 ? (
        renderVirtualizedList
      ) : (
        <div style={{ padding: 8, textAlign: 'center' }}>No options</div>
      )}
    </>
  );
};
